// Contrail V3.8.0 — Admin → Devices → Command presets (admins).
// A preset is a named, ready-made command for a protocol/model, e.g. "ST-901 · Set reporting interval".
// New presets start "Not verified": bench-test them, then tick Verified (and "Technicians may send" if appropriate).
import { FormEvent, useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, type CommandPreset } from "../../lib/admin";
import { Drawer, Field, Notice } from "./ui";

const TYPES = ["custom", "positionPeriodic", "positionSingle", "engineStop", "engineResume", "alarmArm", "alarmDisarm", "rebootDevice", "getVersion", "setTimezone"];

export default function CommandPresets({ onClose }: { onClose: () => void }) {
  const [list, setList] = useState<CommandPreset[] | null>(null);
  const [edit, setEdit] = useState<CommandPreset | "new" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => {
    const { data } = await supabase.from("command_presets").select("*").order("protocol").order("model").order("sort");
    setList((data ?? []) as CommandPreset[]);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Drawer title="Command presets" onClose={onClose} footer={<><button className="btn-ghost" onClick={onClose}>Close</button>
      <button className="btn-primary" onClick={() => setEdit("new")}>+ New preset</button></>}>
      <div className="aform">
        <p className="sub wide">Ready-made commands shown in each device's Commands panel when the protocol and model match. Technicians only see presets that are verified, marked “Technicians may send” and not engine commands.</p>
        {msg && <div className="wide"><Notice onClose={() => setMsg(null)}>{msg}</Notice></div>}
        <ul className="preset-list wide">
          {list === null && <li className="sub">Loading…</li>}
          {list?.map((p) => (
            <li key={p.id} onClick={() => setEdit(p)} role="button" className={p.active ? "" : "off"}>
              <div><b>{p.label}</b><div className="sub">{[p.model, p.protocol].filter(Boolean).join(" · ") || "Any tracker"} · {p.type}{p.channel === "sms" ? " · SMS" : ""}</div></div>
              <span className="preset-tags">
                {p.danger && <span className="apill bad">Engine</span>}
                <span className={`apill ${p.verified ? "ok" : "warn"}`}>{p.verified ? "Verified" : "Not verified"}</span>
                {p.tech_allowed && p.verified && !p.danger && <span className="apill info">Technicians</span>}
                {!p.active && <span className="apill plain">Hidden</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {edit && <PresetForm preset={edit === "new" ? null : edit} onClose={() => setEdit(null)} onSaved={async (t) => { setEdit(null); setMsg(t); await load(); }} />}
    </Drawer>
  );
}

function PresetForm({ preset, onClose, onSaved }: { preset: CommandPreset | null; onClose: () => void; onSaved: (t: string) => void }) {
  const [f, setF] = useState({
    label: preset?.label ?? "", description: preset?.description ?? "", protocol: preset?.protocol ?? "h02", model: preset?.model ?? "",
    channel: preset?.channel ?? "data", type: preset?.type ?? "custom", sort: String(preset?.sort ?? 100), notes: preset?.notes ?? "",
    danger: preset?.danger ?? false, verified: preset?.verified ?? false, tech_allowed: preset?.tech_allowed ?? false, active: preset?.active ?? true,
    attributes: JSON.stringify(preset?.attributes ?? { data: "*HQ,{uniqueId},..." }, null, 1),
    params: JSON.stringify(preset?.params ?? [], null, 1),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const tick = (k: "danger" | "verified" | "tech_allowed" | "active") => (e: { target: { checked: boolean } }) => setF({ ...f, [k]: e.target.checked });

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    let attributes: unknown, params: unknown;
    try { attributes = JSON.parse(f.attributes || "{}"); } catch { return setErr("Attributes must be valid JSON, e.g. {\"data\": \"*HQ,{uniqueId},D1,{time},30#\"}"); }
    try { params = JSON.parse(f.params || "[]"); } catch { return setErr("Parameters must be valid JSON, e.g. []"); }
    setBusy(true);
    try {
      const body = { label: f.label, description: f.description || null, protocol: f.protocol || null, model: f.model || null, channel: f.channel,
        type: f.type, sort: Number(f.sort), notes: f.notes || null, danger: f.danger, verified: f.verified, tech_allowed: f.tech_allowed, active: f.active, attributes, params };
      await adminApi(preset ? "PATCH" : "POST", preset ? `command-presets/${preset.id}` : "command-presets", body);
      onSaved(`${preset ? "Saved" : "Added"} “${f.label}”.`);
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }

  return (
    <Drawer title={preset ? preset.label : "New command preset"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" form="preset-form" disabled={busy}>{busy ? "Saving…" : "Save"}</button></>}>
      <form id="preset-form" className="aform" onSubmit={save}>
        <Field label="Name" wide><input value={f.label} onChange={set("label")} required maxLength={80} placeholder="e.g. Set reporting interval" /></Field>
        <Field label="What it does" wide><input value={f.description} onChange={set("description")} maxLength={300} /></Field>
        <Field label="Protocol" hint="Empty = any"><input value={f.protocol} onChange={set("protocol")} maxLength={30} placeholder="h02" /></Field>
        <Field label="Model" hint="Empty = any model of the protocol"><input value={f.model} onChange={set("model")} maxLength={60} placeholder="ST-901" /></Field>
        <Field label="Command type" hint="Use custom to send your own text.">
          <select value={TYPES.includes(f.type) ? f.type : "__other"} onChange={(e) => setF({ ...f, type: e.target.value === "__other" ? "" : e.target.value })}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}<option value="__other">Other…</option>
          </select>
          {!TYPES.includes(f.type) && <input value={f.type} onChange={set("type")} placeholder="Traccar command type" required />}
        </Field>
        <Field label="Channel">
          <select value={f.channel} onChange={set("channel")}><option value="data">Data (GPRS)</option><option value="sms">SMS (not set up yet)</option></select>
        </Field>
        <Field label="Attributes (JSON)" wide hint={<>For custom: {"{\"data\": \"*HQ,{uniqueId},D1,{time},30#\"}"}. Use {"{param}"} for values asked when sending, {"{uniqueId}"} and {"{time}"} are filled in automatically.</>}>
          <textarea className="mono" rows={3} value={f.attributes} onChange={set("attributes")} />
        </Field>
        <Field label="Values asked when sending (JSON)" wide hint={<>e.g. {"[{\"key\":\"seconds\",\"label\":\"Report every\",\"kind\":\"number\",\"min\":10,\"max\":3600,\"default\":30,\"unit\":\"s\"}]"}</>}>
          <textarea className="mono" rows={3} value={f.params} onChange={set("params")} />
        </Field>
        <fieldset className="wide aradio">
          <legend>Safety</legend>
          <label><input type="checkbox" checked={f.danger} onChange={tick("danger")} /><span><b>Engine / immobiliser command</b> — only when stopped, typed confirmation, never queued, admins only.</span></label>
          <label><input type="checkbox" checked={f.verified} onChange={tick("verified")} /><span><b>Verified</b> — tested on this model and it does what it says.</span></label>
          <label><input type="checkbox" checked={f.tech_allowed} onChange={tick("tech_allowed")} disabled={f.danger} /><span><b>Technicians may send</b> (once verified).</span></label>
          <label><input type="checkbox" checked={f.active} onChange={tick("active")} /><span><b>Show</b> in device Commands panels.</span></label>
        </fieldset>
        <Field label="Order"><input type="number" value={f.sort} onChange={set("sort")} min={0} max={9999} /></Field>
        <Field label="Notes (staff)" wide><textarea rows={2} value={f.notes} onChange={set("notes")} maxLength={1000} placeholder="Exact text sent, firmware tested, reply seen…" /></Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}
